import React, { useContext, useRef, useState } from 'react'
import {
  AppBar,
  Divider,
  IconButton,
  Toolbar,
  Typography,
  styled,
  Skeleton,
} from '@mui/material'
import { BackButton } from '$common/buttons'
import { MenuIcon } from '$common/icons/menu'
import { DownloadIcon } from '$common/icons/download'
import {
  ContextMenuItem,
  ContextMenuList,
} from '$comp/_app/components/chat/components/body/components/context-menu/components'
import { EditOutlinedIcon, ForwardIcon, RemoveIcon } from '$common/icons'
import { DirectionContext } from '$setup/root'
import { SharedMediaIcon } from '$common/icons/shared-media'
import { t } from '$i18n'

const TitleTypography = styled(Typography)`
  margin-right: 7px;
  flex-grow: 1;
`

const StyledDivider = styled(Divider)(() => ({
  '& .MuiDivider-wrapper': {
    display: 'none',
  },
}))

interface Prop {
  title: string
  onBack: () => unknown
  functions?: {
    onDownload?: () => unknown
    onRemove?: () => unknown
    onForward?: () => unknown
    onShowSharedMedia?: () => unknown
    onEdit?: () => unknown
  }
  loading?: boolean
}

export function AppBarBackTitleActionMenu({
  title,
  onBack,
  functions = {},
  loading = false,
}: Prop) {
  const {
    onShowSharedMedia,
    onDownload,
    onForward,
    onRemove,
    onEdit,
  } = functions

  const menuRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [menuLocation, setMenuLocation] = useState({ xAxis: 0, yAxis: 0 })

  const direction = useContext(DirectionContext)

  return (
    <AppBar color="transparent" dir={direction} sx={{ textAlign: 'right' }}>
      <Toolbar sx={{ backgroundColor: 'transparent' }}>
        <BackButton onClick={onBack} color="neutral000" />
        {loading ? (
          <div style={{ display: 'flex', width: '100%', gap: '4px' }}>
            <Skeleton
              sx={{ backgroundColor: 'grey.900' }}
              variant="rounded"
              height="12px"
              width="48px"
            />
          </div>
        ) : (
          <TitleTypography color="white" variant="subtitle1">
            {title}
          </TitleTypography>
        )}
        {(onEdit || onDownload) && (
          <IconButton onClick={onEdit || onDownload}>
            {onEdit ? (
              <EditOutlinedIcon sx={{ color: 'white' }} />
            ) : (
              <DownloadIcon sx={{ color: 'white' }} />
            )}
          </IconButton>
        )}
        {(onDownload || onForward || onShowSharedMedia || onRemove) && (
          <IconButton
            ref={menuRef}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              setMenuLocation({ xAxis: e.clientX, yAxis: e.clientY })
              setIsMenuOpen(!isMenuOpen)
            }}
          >
            <MenuIcon sx={{ color: 'white' }} />
          </IconButton>
        )}
      </Toolbar>
      <ContextMenuList
        onClose={() => setIsMenuOpen(false)}
        open={isMenuOpen}
        xAxis={menuLocation.xAxis}
        yAxis={menuLocation.yAxis}
      >
        {onForward && (
          <ContextMenuItem text={t('contextMenu.forward')} onClick={onForward}>
            <ForwardIcon sx={{ color: 'secondary.main' }} />
          </ContextMenuItem>
        )}
        {onShowSharedMedia && (
          <ContextMenuItem
            text={t('contextMenu.showSharedMedia')}
            onClick={onShowSharedMedia}
          >
            <SharedMediaIcon />
          </ContextMenuItem>
        )}
        {onEdit && onDownload && (
          <ContextMenuItem
            text={t('contextMenu.saveToDownloads')}
            onClick={onDownload}
          >
            <DownloadIcon />
          </ContextMenuItem>
        )}

        {onRemove && <StyledDivider />}
        {onRemove && (
          <ContextMenuItem text={t('common.remove')} onClick={onRemove}>
            <RemoveIcon />
          </ContextMenuItem>
        )}
      </ContextMenuList>
    </AppBar>
  )
}

AppBarBackTitleActionMenu.defaultProps = {
  functions: {},
  loading: false,
}
